import type { Fetch } from './chain.ts';

/**
 * GLEIF, the global LEI index (https://api.gleif.org, CORS-open, no key).
 * Onboarding starts from the company's LEI: GLEIF says which legal entity it
 * is, and the company's Concordium identity proof must reveal the same LEI,
 * legal name and country (SPEC.md §9).
 */
export const GLEIF_API = 'https://api.gleif.org/api/v1/lei-records';

export type GleifEntity = {
  lei: string;
  legalName: string;
  /** ISO 3166-1 alpha-2 of the legal address. */
  country: string;
  /** Jurisdiction (often the same as the country, e.g. "DK"). */
  jurisdiction: string | null;
  /** The business registry number GLEIF has (for a Danish company: the CVR). */
  registeredAs: string | null;
  city: string | null;
  entityStatus: string;
  registrationStatus: string;
  nextRenewal: string | null;
};

/** ISO 17442: 20 characters, [0-9A-Z]{18}[0-9]{2}, check digits by ISO 7064 MOD 97-10. */
export function isValidLei(lei: string): boolean {
  if (!/^[0-9A-Z]{18}[0-9]{2}$/.test(lei)) return false;
  let rem = 0;
  for (const ch of lei) {
    const v = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1;
}

export function normaliseLei(input: string): string {
  return input.replace(/\s+/g, '').toUpperCase();
}

/** Turns a GLEIF `lei-records/<lei>` answer into the fields NetID uses. */
export function parseGleifRecord(body: unknown): GleifEntity {
  const d = (body as { data?: { id?: string; attributes?: any } })?.data;
  const a = d?.attributes;
  if (!d || !a?.entity?.legalName?.name) throw new Error('GLEIF answer has no legal entity');
  const e = a.entity;
  return {
    lei: String(a.lei ?? d.id),
    legalName: String(e.legalName.name),
    country: String(e.legalAddress?.country ?? e.headquartersAddress?.country ?? ''),
    jurisdiction: e.jurisdiction ? String(e.jurisdiction) : null,
    registeredAs: e.registeredAs ? String(e.registeredAs) : null,
    city: e.legalAddress?.city ? String(e.legalAddress.city) : null,
    entityStatus: String(e.status ?? ''),
    registrationStatus: String(a.registration?.status ?? ''),
    nextRenewal: a.registration?.nextRenewalDate ? String(a.registration.nextRenewalDate) : null,
  };
}

export async function lookupLei(lei: string, fetchFn: Fetch, base = GLEIF_API): Promise<GleifEntity> {
  const id = normaliseLei(lei);
  if (!isValidLei(id)) throw new Error(`${id} is not a valid LEI (20 characters with ISO 17442 check digits)`);
  const res = await fetchFn(`${base}/${id}`, { headers: { accept: 'application/vnd.api+json' } });
  if (res.status === 404) throw new Error(`GLEIF has no record for LEI ${id}`);
  if (!res.ok) throw new Error(`GLEIF answered HTTP ${res.status}`);
  const e = parseGleifRecord(await res.json());
  if (e.lei !== id) throw new Error(`GLEIF answered for ${e.lei}, not ${id}`);
  return e;
}

/**
 * Compares company names the way a person would: case, accents, punctuation,
 * spacing and the spelling of the legal form do not matter
 * ("PROJEKT Y APS" = "Projekt Y ApS" = "Projekt-Y Aps."), the words do.
 */
export function normaliseCompanyName(name: string): string {
  let s = name.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase();
  // Letters NFKD does not split.
  s = s.replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/å/g, 'a').replace(/ß/g, 'ss').replace(/ł/g, 'l').replace(/đ/g, 'd').replace(/þ/g, 'th');
  s = s.replace(/&/g, ' and ');
  // Legal forms written with a slash: "A/S", "I/S", "K/S", "P/S".
  s = s.replace(/\b([a-z])\s*\/\s*([a-z])\b/g, '$1$2');
  s = s.replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  // Dotted legal forms at the end: "S.A.", "G.m.b.H.", "B.V.", "N.V.".
  s = s.replace(/ g m b h$/, ' gmbh').replace(/ ([sbn]) ([av])$/, ' $1$2');
  return s;
}

export function normaliseCountry(c: string | null | undefined): string {
  return String(c ?? '').trim().toUpperCase();
}

/** Business numbers compare without spaces, dashes, dots and a leading country prefix ("DK45749754" = "45749754"). */
export function normaliseBusinessNumber(n: string | null | undefined, country?: string | null): string {
  let s = String(n ?? '').toUpperCase().replace(/[\s.\-/]/g, '');
  const cc = normaliseCountry(country);
  if (cc && s.startsWith(cc) && /^\d/.test(s.slice(cc.length))) s = s.slice(cc.length);
  return s;
}

export type CompanyMatch = { ok: boolean; problems: string[] };

/**
 * Does the identity the wallet revealed belong to the entity GLEIF names for
 * the LEI the company entered? `revealed` holds the Concordium attributes
 * (legalName, legalCountry, businessNumber, lei), exactly as the proof
 * revealed them.
 */
export function matchGleif(enteredLei: string, gleif: GleifEntity, revealed: Record<string, string | undefined>): CompanyMatch {
  const problems: string[] = [];
  const lei = normaliseLei(enteredLei);
  if (gleif.lei !== lei) problems.push(`GLEIF's record is for ${gleif.lei}, not the entered LEI ${lei}`);
  if (!revealed.lei) problems.push('the proof does not reveal the LEI');
  else if (normaliseLei(revealed.lei) !== lei) problems.push(`the proof reveals LEI ${revealed.lei}, not the entered ${lei}`);
  if (!revealed.legalName) problems.push('the proof does not reveal the legal name');
  else if (normaliseCompanyName(revealed.legalName) !== normaliseCompanyName(gleif.legalName)) {
    problems.push(`the proof's legal name "${revealed.legalName}" is not GLEIF's "${gleif.legalName}"`);
  }
  if (!revealed.legalCountry) problems.push('the proof does not reveal the country');
  else {
    const c = normaliseCountry(revealed.legalCountry);
    if (c !== normaliseCountry(gleif.country) && c !== normaliseCountry(gleif.jurisdiction)) {
      problems.push(`the proof's country ${revealed.legalCountry} is not GLEIF's ${gleif.country}`);
    }
  }
  if (!revealed.businessNumber) problems.push('the proof does not reveal the business number');
  else if (gleif.registeredAs && normaliseBusinessNumber(revealed.businessNumber, revealed.legalCountry) !== normaliseBusinessNumber(gleif.registeredAs, gleif.country)) {
    problems.push(`the proof's business number ${revealed.businessNumber} is not GLEIF's ${gleif.registeredAs}`);
  }
  return { ok: problems.length === 0, problems };
}
